/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from "@mui/material/Box";
import React, { useEffect, useState, useRef } from "react";
import Header from "components/Header";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useNotifications } from "providers/Notification";
import { useMutation } from "@apollo/client";
import { useForm, UseFormReturn, Controller } from "react-hook-form";
import BackButton from "components/BackButton/BackButton";
import { ONBOARD_ATHLETE } from "queries/athlete";
import { EMAIL_REGEX } from "constant";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  differenceInDays,
  differenceInYears,
  format,
  isValid,
  parse,
} from "date-fns";
import { states } from "./stateData";
import background from "assets/images/SignUp-Successv2.png";
import moment from "moment";
import { insertApiResponseDataDog, insertApiResponseDataDogError } from "utils";
import { Grid } from "semantic-ui-react";
import { documentType } from "./documentType";
import { FileUpload } from "../../../components/FileUpload/fileupload";
import { Post, Put } from "providers/Fetch";
import useUser from "hooks/useUser";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getAthleteCkoVerificationStatus,
  getPaymentRequirementsStatus,
} from "../helper";
import { StripeVerificationStatus } from "types";
import PaymentVerificationErrorDialog from "./PaymentVerificationErrorDialog";

interface PersonalDetailsFormValues {
  firstName: string;
  lastName: string;
  zip: string;
  dob: string;
  personalIdentificationNumber: string;
  email: string;
  payoutType: string;
  bankAccountType: string;
  nameOnAccount: string;
  phone: string;
  routingNumber: string;
  state: string;
  city: string;
  bankAddress: string;
  bankZip: string;
  addressLine1: string;
  addressLine2: string;
  accountNumber: string;
  documentType: string;
  documentFileFront: File | null;
  documentFileBack: File | null;
  bankFile: File | null;
}

function PersonalDetails() {
  const notify = useNotifications();
  const [step, setStep] = useState(0);
  // const [onboardAthlete] = useMutation(ONBOARD_ATHLETE);
  const [LoginUserObject] = useState(
    JSON.parse(localStorage.getItem("LoginUserObject") || "")
  );
  // const [city, setCity] = useState("")
  // const myHeaders = new Headers();
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const { isBankVerificationFailed } = getAthleteCkoVerificationStatus(user);
  const { hasProfileFieldsDue, hasBankFieldsDue } =
    getPaymentRequirementsStatus(user);
  
  // State for controlling the verification error dialog
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  // Flag to track if user has already acknowledged the dialog
  const [hasAcknowledgedDialog, setHasAcknowledgedDialog] = useState(false);
  
  // Refs for form fields to focus after clearing
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const addressLine1Ref = useRef<HTMLInputElement>(null);
  const addressLine2Ref = useRef<HTMLInputElement>(null);
  const cityRef = useRef<HTMLInputElement>(null);
  const stateRef = useRef<HTMLInputElement>(null);
  const zipCodeRef = useRef<HTMLInputElement>(null);
  const dobRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const personalIdentificationNumberRef = useRef<HTMLInputElement>(null);
  const documentTypeRef = useRef<HTMLInputElement>(null);
  const bankAccountTypeRef = useRef<HTMLInputElement>(null);
  const routingNumberRef = useRef<HTMLInputElement>(null);
  const accountNumberRef = useRef<HTMLInputElement>(null);

  const form = useForm<PersonalDetailsFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      addressLine1: "",
      addressLine2: "",
      zip: "",
      dob: "",
      phone: "",
      personalIdentificationNumber: "",
      email: LoginUserObject.email || "",
      payoutType: "ACH",
      bankAccountType: "",
      routingNumber: "",
      state: "",
      city: "",
      accountNumber: "",
      documentType: "",
      documentFileFront: null,
      documentFileBack: null,
      bankFile: null,
    },
    mode: "all",
  });

  // Function to clear fields that are due
  const clearDueFields = () => {
    if (!user?.paymentRequirements) return;
    
    const paymentRequirements = JSON.parse(user.paymentRequirements);
    const currentlyDue = paymentRequirements?.currently_due || [];
    
    // Clear profile fields that are due
    if (currentlyDue.includes("individual.first_name") || currentlyDue.includes("individual.address.first_name")) {
      form.setValue("firstName", "");
      firstNameRef.current?.focus();
    }
    if (currentlyDue.includes("individual.last_name") || currentlyDue.includes("individual.address.last_name")) {
      form.setValue("lastName", "");
      if (!firstNameRef.current?.focus()) lastNameRef.current?.focus();
    }
    if (currentlyDue.includes("individual.address.line1")) {
      form.setValue("addressLine1", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus()) addressLine1Ref.current?.focus();
    }
    if (currentlyDue.includes("individual.address.line2")) {
      form.setValue("addressLine2", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus()) addressLine2Ref.current?.focus();
    }
    if (currentlyDue.includes("individual.address.city")) {
      form.setValue("city", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus()) cityRef.current?.focus();
    }
    if (currentlyDue.includes("individual.address.state")) {
      form.setValue("state", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus()) stateRef.current?.focus();
    }
    if (currentlyDue.includes("individual.address.postal_code")) {
      form.setValue("zip", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus()) zipCodeRef.current?.focus();
    }
    if (currentlyDue.includes("individual.dob")) {
      form.setValue("dob", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus() && !zipCodeRef.current?.focus()) dobRef.current?.focus();
    }
    if (currentlyDue.includes("individual.phone")) {
      form.setValue("phone", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus() && !zipCodeRef.current?.focus() && !dobRef.current?.focus()) phoneRef.current?.focus();
    }
    if (currentlyDue.includes("individual.id_number")) {
      form.setValue("personalIdentificationNumber", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus() && !zipCodeRef.current?.focus() && !dobRef.current?.focus() && !phoneRef.current?.focus()) personalIdentificationNumberRef.current?.focus();
    }
    if (currentlyDue.includes("individual.verification.document")) {
      form.setValue("documentType", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !addressLine2Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus() && !zipCodeRef.current?.focus() && !dobRef.current?.focus() && !phoneRef.current?.focus() && !personalIdentificationNumberRef.current?.focus()) documentTypeRef.current?.focus();
    }
    if (currentlyDue.includes("individual.verification.document.front")) {
      form.setValue("documentFileFront", null);
    }
    if (currentlyDue.includes("individual.verification.document.back")) {
      form.setValue("documentFileBack", null);
    }
    
    // Clear bank fields that are due
    if (currentlyDue.includes("external_account") || 
        currentlyDue.includes("documents.bank_account_ownership_verification.files")) {
      form.setValue("bankAccountType", "");
      form.setValue("routingNumber", "");
      form.setValue("accountNumber", "");
      if (!firstNameRef.current?.focus() && !lastNameRef.current?.focus() && !addressLine1Ref.current?.focus() && !cityRef.current?.focus() && !stateRef.current?.focus() && !zipCodeRef.current?.focus() && !dobRef.current?.focus() && !phoneRef.current?.focus() && !personalIdentificationNumberRef.current?.focus() && !documentTypeRef.current?.focus()) bankAccountTypeRef.current?.focus();
    }
    if (currentlyDue.includes("external_account_verification_document") || 
        currentlyDue.includes("documents.bank_account_ownership_verification.files")) {
      form.setValue("bankFile", null);
    }
    
    // Trigger form validation to show errors for empty fields
    form.trigger();
  };

  // Check if verification dialog should be shown (only if not already acknowledged)
  useEffect(() => {
    if (!hasAcknowledgedDialog &&
        user?.paymentConnectedAccountId &&
        user?.paymentVerificationStatus === StripeVerificationStatus?.PENDING &&
        (hasBankFieldsDue || hasProfileFieldsDue)) {
      setShowVerificationDialog(true);
    } else {
      setShowVerificationDialog(false);
    }
  }, [user, hasBankFieldsDue, hasProfileFieldsDue, hasAcknowledgedDialog]);

  const bankBInfoPrefilling = () => {
    // Only prefill bank information if bank fields are NOT due
    form.setValue(
      "bankAccountType",
      (user?.ckoBankAccountType as any)
        ? user?.ckoBankAccountType === "current"
          ? "CHECKING"
          : (user?.ckoBankAccountType as any)
        : ""
    );
    form.setValue("routingNumber", user?.ckoRoutingNumber as any | "");
  };

  const profileInfoPrefilling = () => {
    form.setValue("firstName", (user?.ckoFirstName as any) || "");
    form.setValue("lastName", user?.ckoLastName as any | "");
    form.setValue("addressLine1", (user?.ckoAddressLine1 as any) || "");
    form.setValue("addressLine2", (user?.ckoAddressLine2 as any) || "");
    form.setValue("zip", (user?.ckoZip as any) || "");
    form.setValue(
      "dob",
      user?.ckoDob ? (moment(user?.ckoDob).format("MM/DD/YYYY") as any) : ""
    );
    form.setValue("phone", (user?.ckoPhone as any) || "");
    form.setValue("state", (user?.ckoState as any) || "");
    form.setValue("city", (user?.ckoCity as any) || "");
    form.setValue("personalIdentificationNumber", "");
  };
  const profilePrefilingBluesnap = () => {
    form.setValue("firstName", (user?.firstName as any) || "");
    form.setValue("lastName", user?.lastName as any | "");
    form.setValue("zip", (user?.zipCode as any) || "");
    form.setValue(
      "dob",
      user?.birthday ? (moment(user?.birthday).format("MM/DD/YYYY") as any) : ""
    );
    form.setValue("phone", (user?.phoneNumber as any) || "");
  };
  const updatebankDetailsCall = (formdata: any) => {
    Put(
      `${process.env.REACT_APP_SERVER_URI}/stripe/athlete/bank-info`,
      formdata,
      true
    )
      .then(async (response) => response)
      .then((result: any) => {
        setLoading(false);
        insertApiResponseDataDog(
          "updateAthleteBankAccountInfoApi(success)",
          result,
          "info"
        );
        if (result.status > 300) {
          try {
            const responsAry: [
              { code: string; errorName: string; description: string }
            ] = JSON.parse(result.data) || [];
            const errorMessage = `${result.status +
              " : " +
              (typeof responsAry === "string" || responsAry instanceof String
                ? responsAry
                : responsAry[responsAry.length - 1].description)
              }`;
            notify.error(errorMessage);
          } catch (e) {
            const responsAry: { status: string; data: string } = result;
            const errorMessage = `${responsAry.status + " : " + responsAry.data
              }`;
            insertApiResponseDataDog(
              "updateAthleteBankAccountInfoApi(error)",
              errorMessage,
              "error"
            );
            insertApiResponseDataDogError(
              errorMessage,
              "updateAthleteBankAccountInfoApi(error)"
            );
            notify.error(errorMessage);
          }
        } else {
          notify.success(result?.data || "");
          user?.refetch();
          setStep(3);
        }
      })
      .catch((error) => {
        setLoading(false);
        insertApiResponseDataDogError(
          error,
          "updateAthleteBankAccountInfoApi(error)"
        );
        insertApiResponseDataDog(
          "updateAthleteBankAccountInfoApi(error)",
          error,
          "error"
        );
        notify.error(error);
      });
  };

  const individualBankInfoAndProfileInfo = async (
    FormObject: PersonalDetailsFormValues
  ) => {
    let formdata = new FormData();
    if (
      user?.ckoSubEntityDdStatus == null ||
      ["requirements_due", "rejected"].includes(
        user?.ckoSubEntityDdStatus as any
      )
    ) {
      formdata.append("addressLine1", FormObject.addressLine1);
      formdata.append("addressLine2", FormObject.addressLine2);
      formdata.append("city", FormObject.city);
      formdata.append("dob", FormObject.dob);
      formdata.append("firstName", FormObject.firstName);
      formdata.append("lastName", FormObject.lastName);
      formdata.append(
        "personalIdentificationNumber",
        FormObject.personalIdentificationNumber
      );
      formdata.append("phone", FormObject.phone);
      formdata.append("routingNumber", FormObject.routingNumber);
      formdata.append("state", FormObject.state);
      formdata.append("zip", FormObject.zip);
      formdata.append(
        "identityVerificationDocumentType",
        FormObject.documentType
      );
      formdata.append(
        "identityVerificationDocumentFileFront",
        FormObject.documentFileFront as any
      );
      formdata.append(
        "identityVerificationDocumentFileBack",
        FormObject.documentType === "passport"
          ? null
          : (FormObject.documentFileBack as any)
      );

      Post(
        `${process.env.REACT_APP_SERVER_URI}/checkout/athlete/profile-info`,
        formdata,
        true
      )
        .then(async (response) => response)
        .then((result: any) => {
          setLoading(false);
          insertApiResponseDataDog(
            "onboardAthleteWithCkoApi(success)",
            result,
            "info"
          );
          if (result.status > 300) {
            try {
              const responsAry: [
                { code: string; errorName: string; description: string }
              ] = JSON.parse(result.data) || [];
              const errorMessage = `${result.status +
                " : " +
                (typeof responsAry === "string" || responsAry instanceof String
                  ? responsAry
                  : responsAry[responsAry.length - 1].description)
                }`;
              notify.error(errorMessage);
            } catch (e) {
              const responsAry: { status: string; data: string } = result;
              const errorMessage = `${responsAry.status + " : " + responsAry.data
                }`;
              insertApiResponseDataDog(
                "onboardAthleteWithCkoApi(error)",
                errorMessage,
                "error"
              );
              insertApiResponseDataDogError(
                errorMessage,
                "onboardAthleteWithCkoApi(error)"
              );
              notify.error(errorMessage);
              if (
                responsAry.data ==
                "User already onboarded with Checkout account"
              ) {
                window.location.replace("/home");
              }
            }
          } else {
            if (result) {
              if (!isBankVerificationFailed) {
                user?.refetch();
                setStep(3);
              } else {
                formdata = new FormData();
                formdata.append(
                  "bankStatementDocumentFileFront",
                  FormObject.bankFile as any
                );
                formdata.append("bankAccountType", FormObject.bankAccountType);
                formdata.append("accountNumber", FormObject.accountNumber);
                formdata.append("routingNumber", FormObject.routingNumber);
                updatebankDetailsCall(formdata);
              }
            }
          }
        })
        .catch((error) => {
          setLoading(false);
          insertApiResponseDataDogError(
            error,
            "onboardAthleteWithCkoApi(error)"
          );
          insertApiResponseDataDog(
            "onboardAthleteWithCkoApi(error)",
            error,
            "error"
          );
          notify.error(error);
        });
    } else if (
      !["verified", "pending"].includes(user?.ckoSubEntityBankStatus as any)
    ) {
      formdata.append(
        "bankStatementDocumentFileFront",
        FormObject.bankFile as any
      );
      formdata.append("bankAccountType", FormObject.bankAccountType);
      formdata.append("accountNumber", FormObject.accountNumber);
      formdata.append("routingNumber", FormObject.routingNumber);
      updatebankDetailsCall(formdata);
    }
  };

  const updatePaymentRequirements = (formData: any, user: any) => {
    if (!formData) {
      notify.error("Invalid form data for payment requirements update.");
      return;
    }

    const { hasBankFieldsDue, hasProfileFieldsDue } = getPaymentRequirementsStatus(user);
    const payloadForm = new FormData();

    const appendProfileFields = () => {
      const { individual } = formData;
      if (!individual) return;

      const dobMoment = moment(individual.dob, ["YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"], true);
      if (!dobMoment.isValid()) {
        notify.error("Invalid DOB format when preparing update");
        setLoading(false);
        return false;
      }

      const dobString = dobMoment.format("DD-MM-YYYY");

      const profileFields = [
        ["addressLine1", individual.addressLine1],
        ["addressLine2", individual.addressLine2],
        ["city", individual.city],
        ["dob", dobString],
        ["firstName", individual.firstName],
        ["lastName", individual.lastName],
        ["personalIdentificationNumber", individual.personalIdentificationNumber],
        ["phone", individual.phone],
        ["state", individual.state],
        ["zip", individual.zip],
      ];

      profileFields.forEach(([key, value]) => payloadForm.append(key, value));

      const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;
      const hasDocumentFieldsDue = paymentRequirements?.currently_due?.some((field: string) =>
        field.startsWith("individual.verification.")
      ) || false;

      if (!user?.paymentConnectedAccountId || hasDocumentFieldsDue) {
        payloadForm.append("identityVerificationDocumentType", individual.documentType);
        payloadForm.append("identityVerificationDocumentFileFront", individual.documentFileFront as any);
        payloadForm.append(
          "identityVerificationDocumentFileBack",
          individual.documentType === "passport" ? null : (individual.documentFileBack as any)
        );
      }

      return true;
    };

    const appendBankFields = () => {
      let source = formData.external_account || formData.external_account_verification_document;
      // Only try to access bank_account_ownership_verification if 'documents' exists
      if (formData.documents && formData.documents.bank_account_ownership_verification && formData.documents.bank_account_ownership_verification.files) {
        source = formData.documents.bank_account_ownership_verification.files;
      }
      if (!source) return;

      const { bankStatementDocumentFileFront, ...accountInfo } = source;
      const key = formData.external_account
        ? "external_account"
        : formData.external_account_verification_document
          ? "external_account_verification_document"
          : (formData.documents && formData.documents.bank_account_ownership_verification && formData.documents.bank_account_ownership_verification.files)
            ? "documents.bank_account_ownership_verification.files"
            : "external_account";

      payloadForm.append(key, JSON.stringify(accountInfo));
      if (bankStatementDocumentFileFront) {
        payloadForm.append("bankStatementDocumentFileFront", bankStatementDocumentFileFront);
      }
    };

    if (hasProfileFieldsDue && !appendProfileFields()) return;
    if (hasBankFieldsDue) appendBankFields();

    Post(`${process.env.REACT_APP_SERVER_URI}/stripe/athlete/update`, payloadForm, true)
      .then((result) => {
        setLoading(false);
        insertApiResponseDataDog("updateAthletePaymentRequirementsApi(success)", result, "info");

        const isError = result?.status >= 400 || result?.data?.status >= 400;
        if (isError) {
          const { status, message, error } = result.data || {};
          const errorMessage =
            message || error
              ? `${status || result.status} : ${message || error}`
              : `${result.status || 400} : ${JSON.stringify(result.data || result)}`;

          notify.error(errorMessage);
          insertApiResponseDataDogError(errorMessage, "updateAthletePaymentRequirementsApi(error)");
        } else {
          notify.success(result?.message || "Payment requirements updated successfully.");
          user?.refetch();
          setStep(3);
        }
      })
      .catch((error) => {
        setLoading(false);
        insertApiResponseDataDogError(error, "updateAthletePaymentRequirementsApi(error)");

        const message = error instanceof SyntaxError && error.message.includes("JSON")
          ? "Invalid response from server. Please try again."
          : `Network error: ${error.message || "Unknown error"}`;
        notify.error(message);
      });
  };

  const onSubmit = async (FormObject: PersonalDetailsFormValues) => {
    const { hasBankFieldsDue, hasProfileFieldsDue } = getPaymentRequirementsStatus(user);

    // DOB Validation and Formatting
    const parsedDob = moment(FormObject.dob, ["YYYY-MM-DD", "DD-MM-YYYY", "MM/DD/YYYY"], true);

    if (!parsedDob.isValid()) {
      form.setValue("dob", "", { shouldValidate: false, shouldTouch: false });
      form.setError(
        "dob",
        { type: "focus", message: "Invalid Date Of Birth" },
        { shouldFocus: true }
      );
      notify.error("Invalid Date Of Birth");
      setStep(1);
      return;
    }

    // Format dob to 'DD-MM-YYYY' for consistent use
    FormObject.dob = parsedDob.format("DD-MM-YYYY");

    // --- 2. Trim string fields (Moved up for consistent data) ---
    FormObject.firstName = FormObject.firstName.trim();
    FormObject.lastName = FormObject.lastName.trim();
    FormObject.addressLine1 = FormObject.addressLine1.trim();
    FormObject.addressLine2 = FormObject.addressLine2.trim();
    FormObject.phone = FormObject.phone.trim();
    FormObject.city = FormObject.city.trim();
    FormObject.state = FormObject.state.trim();
    FormObject.zip = FormObject.zip.trim();
    FormObject.email = FormObject.email.trim();

    // --- 3. Update Payment Requirements Path ---
    const isUpdateRequired =
      user?.paymentConnectedAccountId &&
      user?.paymentVerificationStatus === StripeVerificationStatus?.PENDING &&
      (hasBankFieldsDue || hasProfileFieldsDue);

    if (isUpdateRequired) {
      setLoading(true);
      const formDataForUpdate: any = {};
      const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;

      if (hasProfileFieldsDue) {
        formDataForUpdate.individual = {
          addressLine1: FormObject.addressLine1,
          addressLine2: FormObject.addressLine2,
          city: FormObject.city,
          dob: FormObject.dob,
          firstName: FormObject.firstName,
          lastName: FormObject.lastName,
          personalIdentificationNumber: FormObject.personalIdentificationNumber,
          phone: FormObject.phone,
          state: FormObject.state,
          zip: FormObject.zip,
          documentType: FormObject.documentType,
          documentFileFront: FormObject.documentFileFront,
          documentFileBack: FormObject.documentFileBack,
        };
      }
      if (hasBankFieldsDue) {
        const usesVerificationDocument = paymentRequirements?.currently_due?.includes('external_account_verification.document');
        const ownershipVerificationDocument = paymentRequirements?.currently_due?.includes('documents.bank_account_ownership_verification.files');
        const bankKey = usesVerificationDocument
          ? "external_account_verification_document"
          : ownershipVerificationDocument
            ? "documents.bank_account_ownership_verification.files"
            : "external_account";
        const bankAccountData: any = {
          bank_account_type: FormObject.bankAccountType,
          routing_number: FormObject.routingNumber,
          account_number: FormObject.accountNumber,
        };

        // Add file to the nested object if it exists (updatePaymentRequirements handles FormData append later)
        if (FormObject.bankFile) {
          bankAccountData.bankStatementDocumentFileFront = FormObject.bankFile;
        }

        formDataForUpdate[bankKey] = bankAccountData;
      }

      updatePaymentRequirements(formDataForUpdate, user);
      return;
    }

    if (!user?.paymentConnectedAccountId || (hasBankFieldsDue && hasProfileFieldsDue)) {
      const formdata = new FormData();
      const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;

      // --- Append All Core Fields ---
      formdata.append("accountNumber", FormObject.accountNumber);
      formdata.append("addressLine1", FormObject.addressLine1);
      formdata.append("addressLine2", FormObject.addressLine2);
      formdata.append("bankAccountType", FormObject.bankAccountType);
      formdata.append("city", FormObject.city);
      formdata.append("dob", FormObject.dob); // Already formatted to DD-MM-YYYY
      formdata.append("firstName", FormObject.firstName);
      formdata.append("lastName", FormObject.lastName);
      formdata.append("personalIdentificationNumber", FormObject.personalIdentificationNumber);
      formdata.append("phone", FormObject.phone);
      formdata.append("routingNumber", FormObject.routingNumber);
      formdata.append("state", FormObject.state);
      formdata.append("zip", FormObject.zip);

      // Append Bank Statement File (always included for initial onboarding with bank details)
      if (FormObject.bankFile) {
        formdata.append("bankStatementDocumentFileFront", FormObject.bankFile as any);
      }

      // --- Append Identification Documents ---
      const hasDocumentFieldsDue = paymentRequirements?.currently_due?.some(
        (field: string) => field.startsWith('individual.verification.')
      ) || false;

      // If not connected, or if documents are explicitly due, append them
      if (!user?.paymentConnectedAccountId || hasDocumentFieldsDue) {
        formdata.append("identityVerificationDocumentType", FormObject.documentType);
        formdata.append("identityVerificationDocumentFileFront", FormObject.documentFileFront as any);

        const documentBackFile = FormObject.documentType === "passport" ? null : (FormObject.documentFileBack as any);
        formdata.append("identityVerificationDocumentFileBack", documentBackFile);
      }

      setLoading(true);
      Post(`${process.env.REACT_APP_SERVER_URI}/stripe/athlete/onboard`, formdata, true)
        .then(async (response) => response)
        .then((result: any) => {
          setLoading(false);
          user?.refetch();
          insertApiResponseDataDog("onboardAthleteWithCkoApi(success)", result, "info");

          if (result.status > 300) {
            let errorMessage = "An error occurred during onboarding.";
            try {
              const responsAry: [{ code: string; errorName: string; description: string }] = JSON.parse(result.data) || [];
              const errorDetails = Array.isArray(responsAry) && responsAry.length > 0
                ? responsAry[responsAry.length - 1].description
                : (typeof responsAry === "string" || responsAry instanceof String ? responsAry : JSON.stringify(responsAry));
              errorMessage = `${result.status} : ${errorDetails}`;
            } catch (e) {
              const responsAry: { status: string; data: string } = result;
              errorMessage = `${responsAry.status} : ${responsAry.data}`;
            }

            insertApiResponseDataDog("onboardAthleteWithCkoApi(error)", errorMessage, "error");
            insertApiResponseDataDogError(errorMessage, "onboardAthleteWithCkoApi(error)");
            notify.error(errorMessage);

            if (errorMessage.includes("User already onboarded")) {
              window.location.replace("/home");
            }
          } else {
            setStep(3);
            user?.refetch();
          }
        })
        .catch((error) => {
          user?.refetch();
          setLoading(false);
          insertApiResponseDataDogError(error, "onboardAthleteWithCkoApi(error)");
          insertApiResponseDataDog("onboardAthleteWithCkoApi(error)", error, "error");
          notify.error(error.message || error);
        });
    }
  };

  useEffect(() => {
    if (step === 0) {
      if (!user?.paymentConnectedAccountId) {
        // New user - show both forms
        setStep(1);
        if (user?.athleteOnboarded) {
          profilePrefilingBluesnap();
        }
      } else if (hasProfileFieldsDue && hasBankFieldsDue) {
        // Both profile and bank fields due - show profile form first
        profileInfoPrefilling();
        setStep(1);
      } else if (hasProfileFieldsDue) {
        // Only profile fields due - show profile form
        profileInfoPrefilling();
        setStep(1);
      } else if (hasBankFieldsDue) {
        // Only bank fields due - show bank form directly
        bankBInfoPrefilling();
        setStep(2);
      }
      if (isBankVerificationFailed) {
        bankBInfoPrefilling();
      }
    }
  }, [user]);

  useEffect(() => {
    user?.refetch();
  }, []);

  if (user?.loading) return <CircularProgress />;

  const props = {
    form,
    setStep,
    loading,
    user,
    hasProfileFieldsDue,
    hasBankFieldsDue,
    firstNameRef,
    lastNameRef,
    addressLine1Ref,
    cityRef,
    stateRef,
    zipCodeRef,
    dobRef,
    phoneRef,
    personalIdentificationNumberRef,
    documentTypeRef,
    bankAccountTypeRef,
    routingNumberRef,
    accountNumberRef,
  };
  if (step === 3) {
    return (
      <Stack
        spacing={2}
        sx={{
          position: "absolute",
          top: "0px",
          bottom: "0px",
          left: "0px",
          right: "0px",
          backgroundImage: `url(${background})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          zIndex: "0",
          paddingTop: "100px",
        }}
      >
        <Grid style={{ width: 400, margin: "0 auto" }}>
          <Typography
            variant="h4"
            component="h1"
            align="left"
            sx={{ color: "#fff", fontSize: "2rem", marginBottom: 5 }}
          >{`Welcome, ${form.getValues("firstName")}!`}</Typography>

          <Typography
            sx={{
              color: "#fff",
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              marginBottom: 3,
            }}
            fontSize={16}
            fontWeight={200}
            align="left"
          >
            WHAT’S NEXT?{" "}
          </Typography>

          <Typography
            sx={{
              color: "#fff",
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              marginBottom: 3,
            }}
            fontSize={16}
            fontWeight={200}
            align="left"
          >
            The processor needs to approve your account information and then you
            will begin receiving payments.
          </Typography>
          <Typography
            sx={{
              color: "#fff",
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
            }}
            fontSize={16}
            fontWeight={200}
            align="left"
          >
            This is a one-time process and shouldn’t take longer than 24-48
            hours.{" "}
          </Typography>
          <Box style={{ margin: "0 auto" }}>
            <Button
              variant="contained"
              href="/home"
              sx={{
                background: "#FFF",
                marginTop: 10,
                color: "#592EE5",
                width: "400px",
                ":hover": { backgroundColor: "#f0ebff" },
              }}
            >
              Back To Home{" "}
            </Button>
          </Box>
        </Grid>
      </Stack>
    );
  }

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)} autoComplete="off">
        {step === 1 && (
          <Step1 {...props} isBankVerificationFailed={isBankVerificationFailed} />
        )}
        {step === 2 && (
          <>
            {!user?.paymentConnectedAccountId ||
              (hasProfileFieldsDue && hasBankFieldsDue) ? (
              <BackButton onClick={() => setStep(1)} />
            ) : (
              ""
            )}
            <Step2 {...props} />
          </>
        )}
      </form>
      
      {/* Payment Verification Error Dialog */}
      <PaymentVerificationErrorDialog
        open={showVerificationDialog}
        onClose={() => {
          setShowVerificationDialog(false);
          setHasAcknowledgedDialog(true);
        }}
        onProceed={() => {
          setShowVerificationDialog(false);
          setHasAcknowledgedDialog(true);
          clearDueFields();
        }}
        errorData={user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : {}}
      />
    </>
  );
}

interface FormStepProps {
  form: UseFormReturn<PersonalDetailsFormValues>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  user: any;
  isBankVerificationFailed?: boolean;
  hasProfileFieldsDue?: boolean;
  hasBankFieldsDue?: boolean;
  firstNameRef?: React.RefObject<HTMLInputElement>;
  lastNameRef?: React.RefObject<HTMLInputElement>;
  addressLine1Ref?: React.RefObject<HTMLInputElement>;
  cityRef?: React.RefObject<HTMLInputElement>;
  stateRef?: React.RefObject<HTMLInputElement>;
  zipCodeRef?: React.RefObject<HTMLInputElement>;
  dobRef?: React.RefObject<HTMLInputElement>;
  phoneRef?: React.RefObject<HTMLInputElement>;
  personalIdentificationNumberRef?: React.RefObject<HTMLInputElement>;
  documentTypeRef?: React.RefObject<HTMLInputElement>;
  bankAccountTypeRef?: React.RefObject<HTMLInputElement>;
  routingNumberRef?: React.RefObject<HTMLInputElement>;
  accountNumberRef?: React.RefObject<HTMLInputElement>;
}

const Step1 = ({
  form,
  setStep,
  user,
  loading,
  isBankVerificationFailed,
  hasProfileFieldsDue,
  hasBankFieldsDue,
  firstNameRef,
  lastNameRef,
  addressLine1Ref,
  cityRef,
  stateRef,
  zipCodeRef,
  dobRef,
  phoneRef,
  personalIdentificationNumberRef,
  documentTypeRef,
}: FormStepProps) => {
  const [dateValue, setDateValue] = React.useState<any>(
    form.getValues("dob") || ""
  );
  const { isDirty, errors, isSubmitting, touchedFields } = form.formState;
  const [firstName, setFirstName] = useState(form.getValues("firstName") || "");
  const [lastName, setLastName] = useState(form.getValues("lastName") || "");
  const [LastFourDigits, setLastFourDigits] = useState(
    form.getValues("personalIdentificationNumber") || ""
  );
  const EmailEditable =
    (process.env.REACT_APP_ATHLETE_ONBORDING_EMAIL_EDITABLE || "true") ===
      "true"
      ? false
      : true;
  const [phone, setPhoneNumber] = useState(form.getValues("phone") || "");

  const currentDate = new Date();
  const DateValidation = currentDate.setFullYear(
    currentDate.getFullYear() - 18
  );
  const minDateValidation = currentDate.setFullYear(
    currentDate.getFullYear() - 82
  );
  const minformateDate = moment(minDateValidation).format("MM/DD/YYYY");
  const formateDate = moment(DateValidation).format("yyyy-MM-DD");
  const [error, setError] = useState(false);
  const [ageError, setAgeError] = useState(false);
  const [maxAgeError, setMaxAgeError] = useState(false);
  const [frontFile, setFrontFile] = useState<File | null>(
    form.getValues("documentFileFront") || null
  );
  const [backFile, setBackFile] = useState<File | null>(
    form.getValues("documentFileBack") || null
  );

  const handleChangeZip = (e: any) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === "" || regex.test(e.target.value)) {
      if (e.target.value.length <= 5) {
        form.setValue("zip", e.target.value);
      }
    }
  };
  
  const handleMaxDateChange = (newValue: Date | null) => {
    setDateValue(newValue);

    if (newValue) {
      const currentDate = new Date();
      const age = differenceInDays(currentDate, newValue);
      const years = 100;
      const daysInYear = 365.25;
      const days = years * daysInYear;
      if (age > days) {
        setMaxAgeError(true);
      } else {
        setMaxAgeError(false);
      }
    } else {
      setMaxAgeError(false);
    }
  };
  const handleDateChange = (newValue: Date | null) => {
    setDateValue(newValue);
    if (newValue) {
      const currentDate = new Date();
      const age = differenceInYears(currentDate, newValue);

      if (age < 18) {
        setAgeError(true);
      } else {
        setAgeError(false);
      }
    } else {
      setAgeError(false);
    }
  };

  const handleManualDateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputDate = event.target.value;
    const parsedDate = parse(inputDate, "MM/dd/yyyy", new Date());

    if (inputDate.length === 10 && isValid(parsedDate)) {
      setDateValue(parsedDate);
      setError(false);
      handleDateChange(parsedDate);
      handleMaxDateChange(parsedDate);
    } else {
      setDateValue(null);
      setError(true);
      handleDateChange(null);
      handleMaxDateChange(null);
    }
  };
  const handleChangeLastFourDigits = (e: any) => {
    const regex = /^[0-9\b]+$/;
    if (e.target.value === "" || regex.test(e.target.value)) {
      if (e.target.value.length <= 9) {
        setLastFourDigits(e.target.value);
      }
    }
  };
  const isFirstStepValid = () => {
    const noErrors = () => {
      const {
        firstName,
        lastName,
        zip,
        dob,
        phone,
        personalIdentificationNumber,
        email,
        state,
        city,
        addressLine1,
        documentType
      } = errors;

      const baseFieldsValid =
        !firstName &&
        !lastName &&
        !zip &&
        !dob &&
        !phone &&
        !email &&
        !personalIdentificationNumber &&
        !state &&
        !city &&
        !addressLine1;

      // FIXED: Only check documentType error if document fields are specifically required
      const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;
      const hasDocumentFieldsDue = paymentRequirements?.currently_due?.some(
        (field: string) => field.startsWith('individual.verification.')
        // ) || paymentRequirements?.pending_verification?.some(
        //   (field: string) => field.startsWith('individual.verification.')
      ) || false;

      if (hasDocumentFieldsDue) {
        return baseFieldsValid && !documentType;
      }
      return baseFieldsValid;
    };

    const noEmptyValues = () => {
      const {
        firstName,
        addressLine1,
        lastName,
        zip,
        phone,
        dob,
        personalIdentificationNumber,
        state,
        city,
        documentType
      } = form.getValues();

      const baseFieldsValid =
        firstName?.trim() &&
        lastName?.trim() &&
        zip?.trim() &&
        dob?.trim() &&
        phone?.trim() &&
        personalIdentificationNumber?.trim() &&
        addressLine1?.trim() &&
        state?.trim() &&
        city?.trim();

      // FIXED: Only require documentType if document fields are specifically required
      const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;
      const hasDocumentFieldsDue = paymentRequirements?.currently_due?.some(
        (field: string) => field.startsWith('individual.verification.')
        // ) || paymentRequirements?.pending_verification?.some(
        //   (field: string) => field.startsWith('individual.verification.')
      ) || false;

      if (hasDocumentFieldsDue) {
        return baseFieldsValid && documentType;
      }
      return baseFieldsValid;
    };

    return Boolean(noErrors() && noEmptyValues());
  };
  const handleChangePhoneNumber = (e: any) => {
    const arr = Array.from(e.target.value);
    const regex = /^[0-9\b]+$/;
    if (arr[0] !== "0" || e.target.value === "" || regex.test(e.target.value)) {
      if (e.target.value.length <= 10) {
        setPhoneNumber(e.target.value);
      }
    }
  };

  // Parse payment requirements to check for pending verification
  const paymentRequirements = user?.paymentRequirements ? JSON.parse(user.paymentRequirements) : null;
  const hasDocumentPending = paymentRequirements?.currently_due?.some(
    (field: string) => field.startsWith('individual.verification.')
    // ) ||  paymentRequirements?.pending_verification?.some(
    //   (field: string) => field.startsWith('individual.verification.')
  ) || false;

  // FIXED: Determine if documents should be shown based on specific document requirements
  const hasDocumentFieldsDue = paymentRequirements?.currently_due?.some(
    (field: string) => field.startsWith('individual.verification.')
    // ) || paymentRequirements?.pending_verification?.some(
    //   (field: string) => field.startsWith('individual.verification.')
  ) || false;

  const shouldShowDocuments = !user.paymentConnectedAccountId || hasDocumentFieldsDue || hasDocumentPending;

  return (
    <Box className="ath-personal-details">
      <Header text="Personal Details" />
      <Box sx={{ mt: 4 }} textAlign={"left"} className="personal-details__form">
        <Stack sx={{ mt: 2 }} direction="column" alignItems="flex-start">
          <TextField
            margin="dense"
            label="First Name"
            placeholder="From your Drivers License or State ID"
            InputLabelProps={{
              shrink: true,
            }}
            type="text"
            fullWidth
            autoComplete="none"
            value={firstName}
            inputRef={firstNameRef}
            inputProps={{
              "data-testid": "firstName",
            }}
            error={Boolean(isDirty && errors.firstName)}
            disabled={isSubmitting}
            helperText={(isDirty && errors.firstName?.message) || " "}
            {...form.register("firstName", {
              required: "First Name is required",
              minLength: {
                value: 2,
                message: "First Name should be minimum 2 or above characters.",
              },
              maxLength: {
                value: 50,
                message: "First Name should be maximum 50 or below characters.",
              },
            })}
            onChange={(e) => {
              const regex = /^[A-z\s]+$/;
              if (e.target.value === "" || regex.test(e.target.value)) {
                setFirstName(e.target.value);
              }
            }}
          />
          <TextField
            sx={{ mt: 3 }}
            margin="dense"
            data-testid="Last Name"
            label="Last Name"
            placeholder="From your Drivers License or State ID"
            InputLabelProps={{
              shrink: true,
            }}
            type="text"
            fullWidth
            value={lastName}
            autoComplete="none"
            inputRef={lastNameRef}
            inputProps={{
              "data-testid": "lastName",
            }}
            error={isDirty && Boolean(errors.lastName)}
            disabled={isSubmitting}
            helperText={(isDirty && errors.lastName?.message) || " "}
            {...form.register("lastName", {
              required: "Last Name is required",
              minLength: {
                value: 2,
                message: "Last Name should be minimum 2 or above characters.",
              },
              maxLength: {
                value: 50,
                message: "Last Name should be maximum 50 or below characters.",
              },
            })}
            onChange={(e) => {
              const regex = /^[A-z\s]+$/;
              if (e.target.value === "" || regex.test(e.target.value)) {
                setLastName(e.target.value);
              }
            }}
          />

          <TextField
            sx={{ mt: 3 }}
            margin="dense"
            data-testid="Address Line1"
            label="Address Line1"
            placeholder="From your Drivers License or State ID"
            InputLabelProps={{
              shrink: true,
            }}
            type="text"
            fullWidth
            autoComplete="none"
            inputRef={addressLine1Ref}
            inputProps={{
              "data-testid": "address",
            }}
            error={Boolean(isDirty && errors.addressLine1)}
            disabled={isSubmitting}
            helperText={(isDirty && errors.addressLine1?.message) || " "}
            {...form.register("addressLine1", {
              required: "AddressLine1 is required",
              minLength: {
                value: 5,
                message: "Please enter 5 minimum characters",
              },
            })}
          />
          <TextField
            sx={{ mt: 3 }}
            margin="dense"
            data-testid="Address Line2"
            label="Address Line2"
            placeholder="From your Drivers License or State ID"
            InputLabelProps={{
              shrink: true,
            }}
            type="text"
            fullWidth
            autoComplete="none"
            inputProps={{
              "data-testid": "address",
            }}
            {...form.register("addressLine2")}
          />

          {/* FIX: Replace the TextField for 'state' with a Controller */}
          <Controller
            name="state"
            control={form.control}
            rules={{ required: "State is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                select
                margin="dense"
                label="State"
                placeholder="From your Drivers License or State ID"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                autoComplete="none"
                inputRef={stateRef}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
              >
                {states()?.map((val, key) => (
                  <MenuItem key={key} value={val.value}>
                    {val.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* FIX: Replace the TextField for 'city' with a Controller */}
          <Controller
            name="city"
            control={form.control}
            rules={{ required: "City is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                margin="dense"
                data-testid="City"
                label="City"
                placeholder="From your Drivers License or State ID"
                InputLabelProps={{
                  shrink: true,
                }}
                type="text"
                fullWidth
                autoComplete="none"
                inputRef={cityRef}
                inputProps={{
                  "data-testid": "city",
                }}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
                onChange={(e) => {
                  const regex = /^[A-z\s]+$/;
                  if (e.target.value === "" || regex.test(e.target.value)) {
                    field.onChange(e);
                  }
                }}
              />
            )}
          />

          {/* FIX: Replace the TextField for 'zip' with a Controller */}
          <Controller
            name="zip"
            control={form.control}
            rules={{ 
              required: "Zip Code is required",
              minLength: {
                value: 5,
                message: "Please enter 5 minimum digits number",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                margin="dense"
                data-testid="zip"
                label="Zip Code"
                placeholder="From your Drivers License or State ID"
                InputLabelProps={{
                  shrink: true,
                }}
                type="text"
                fullWidth
                autoComplete="none"
                inputRef={zipCodeRef}
                inputProps={{
                  "data-testid": "zip",
                }}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
                onChange={(e) => {
                  const regex = /^[0-9\b]+$/;
                  if (e.target.value === "" || regex.test(e.target.value)) {
                    if (e.target.value.length <= 5) {
                      field.onChange(e);
                    }
                  }
                }}
              />
            )}
          />

          <Box
            component={"span"}
            width={"100%"}
            onClick={() => {
              if (!form.getValues("dob")) {
                form.setError(
                  "dob",
                  { type: "focus", message: "Date of birth is required" },
                  { shouldFocus: true }
                );
              } else {
                form.clearErrors("dob");
              }
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat={"MM/DD/YYYY"}
                disabled={isSubmitting}
                value={dateValue}
                minDate={minformateDate}
                maxDate={formateDate}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(newValue: any) => {
                  localStorage.setItem("fanBirthDate", newValue);
                  setDateValue(newValue);
                  setError(false);
                  setMaxAgeError(false);
                  setAgeError(false);
                  // form.setValue('birthday', format(new Date(newValue), "MM-dd-yyyy"), { shouldValidate: true })
                  form.setValue(
                    "dob",
                    format(new Date(newValue), "MM/dd/yyyy"),
                    { shouldValidate: true }
                  );
                }}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                renderInput={(params: any) => {
                  return (
                    <TextField
                      error={
                        (isDirty && Boolean(errors.dob)) ||
                        error ||
                        ageError ||
                        maxAgeError
                      }
                      label="Birthday"
                      inputProps={{
                        placeholder: "From your Drivers License or State ID",
                        "data-testid": "birthday",
                        readOnly: false,
                      }}
                      InputProps={params.InputProps}
                      onError={() => isDirty && Boolean(errors.dob)}
                      inputRef={dobRef}
                      {...form.register("dob", {
                        required: "Date of birth is required",
                      })}
                      fullWidth
                      autoComplete="none"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      //helperText={(isDirty && errors.birthday?.message) || error ? 'Date should be MM\DD\YYYY' : ''}
                      helperText={
                        (isDirty && errors.dob?.message) || error
                          ? " Date format should be MM/DD/YYYY"
                          : ageError
                            ? " Must be at least 18 years old"
                            : maxAgeError
                              ? " Age should not be greater than 100 years"
                              : ""
                      }
                      onChange={handleManualDateChange}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            sx={{ mt: 3 }}
            margin="dense"
            data-testid="Last four digits of social security number"
            label="Social Security Number"
            placeholder="Social Security Number"
            InputLabelProps={{
              shrink: true,
            }}
            value={LastFourDigits}
            type="text"
            fullWidth
            autoComplete="none"
            inputRef={personalIdentificationNumberRef}
            inputProps={{
              "data-testid": "personalIdentificationNumber",
            }}
            error={Boolean(isDirty && errors.personalIdentificationNumber)}
            disabled={isSubmitting}
            helperText={
              (isDirty && errors.personalIdentificationNumber?.message) || " "
            }
            {...form.register("personalIdentificationNumber", {
              required:
                "Social Security Number is required",
              minLength: {
                value: 9,
                message:
                  "Social Security Number is required",
              },
            })}
            onChange={handleChangeLastFourDigits}
          />
          <TextField
            sx={{ mt: 3 }}
            margin="dense"
            data-testid="Email"
            label="Email"
            placeholder="Email"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              readOnly: EmailEditable,
            }}
            type="text"
            fullWidth
            autoComplete="none"
            inputProps={{
              "data-testid": "email",
            }}
            error={Boolean(isDirty && errors.email)}
            disabled={
              process.env.REACT_APP_ENV === "prod" ? true : isSubmitting
            }
            helperText={(isDirty && errors.email?.message) || " "}
            {...form.register("email", {
              required: "Email is required",
              pattern: {
                value: EMAIL_REGEX,
                message: "Invalid email address",
              },
            })}
          />
          <TextField
            margin="dense"
            inputProps={{ "data-testid": "phone" }}
            label="Phone Number"
            placeholder="XXXXXXXXXX"
            InputLabelProps={{
              shrink: true,
            }}
            type="text"
            value={phone}
            fullWidth
            autoComplete="none"
            inputRef={phoneRef}
            error={Boolean(isDirty && errors.phone)}
            disabled={isSubmitting}
            helperText={(isDirty && errors.phone?.message) || " "}
            {...form.register("phone", {
              required: "Phone number required",
              minLength: {
                value: 10,
                message: "Phone number should be 10 digits",
              },
            })}
            onChange={(e) => handleChangePhoneNumber(e)}
          />
          {shouldShowDocuments && (
            <>
              {/* Document Type */}
              <TextField
                sx={{ mt: 3 }}
                select
                margin="dense"
                data-testid="Document-Type"
                label="Document Type"
                placeholder="Select"
                InputLabelProps={{ shrink: true }}
                type="text"
                fullWidth
                autoComplete="none"
                defaultValue={form?.getValues("documentType") || ""}
                inputRef={documentTypeRef}
                inputProps={{ "data-testid": "documentType" }}
                error={Boolean(isDirty && errors.documentType)}
                disabled={isSubmitting}
                helperText={(isDirty && errors.documentType?.message) || " "}
                {...form.register("documentType", {
                  required: "Document Type is required",
                })}
                onChange={(e) => {
                  form.setValue("documentType", e.target.value);
                  setBackFile(null);
                  form.setValue("documentFileBack", null);
                }}
              >
                {documentType()?.map((val, key) => (
                  <MenuItem key={key} value={val.value}>
                    {val.name}
                  </MenuItem>
                ))}
              </TextField>

              {/* Upload Front */}
              <FileUpload
                acceptFileType=".jpg,.jpeg,.png"
                setFile={(f) => {
                  setFrontFile(f);
                  form.setValue("documentFileFront", f);
                }}
                file={frontFile}
                maxFileSize={4000}
                formatErrorMessage="JPEG, JPG or PNG"
                btnName="Upload file (Front)"
              />

              {/* Upload Back */}
              {form?.watch("documentType") !== "passport" && (
                <FileUpload
                  acceptFileType=".jpg,.jpeg,.png"
                  setFile={(f) => {
                    setBackFile(f);
                    form.setValue("documentFileBack", f);
                  }}
                  file={backFile}
                  maxFileSize={4000}
                  formatErrorMessage="JPEG, JPG or PNG"
                  btnName="Upload file (Back)"
                />
              )}
            </>
          )}

        </Stack>
      </Box>
      {!user.paymentConnectedAccountId ||
        (user.paymentConnectedAccountId &&
          hasProfileFieldsDue &&
          hasBankFieldsDue) ? (
        // Show "Next" button if:
        // - User has no connected account, OR
        // - User has connected account AND both profile and bank fields are due
        <Button
          sx={{ mt: 8 }}
          type="button"
          fullWidth
          color={"primary"}
          variant="contained"
          onClick={() => setStep(2)}
          disabled={
            !isFirstStepValid() ||
            error ||
            ageError ||
            maxAgeError ||
            isSubmitting ||
            (shouldShowDocuments && !frontFile) ||
            (shouldShowDocuments && form?.watch("documentType") !== "passport" && !backFile) ||
            loading
          }
        >
          Next
        </Button>
      ) : (
        // Otherwise, always show "Submit Request" button
        <Button
          sx={{ mt: 8 }}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          type="submit"
          fullWidth
          color={"primary"}
          variant="contained"
          disabled={
            !isFirstStepValid() ||
            error ||
            ageError ||
            maxAgeError ||
            isSubmitting ||
            (shouldShowDocuments && !frontFile) ||
            (shouldShowDocuments && form?.watch("documentType") !== "passport" && !backFile) ||
            loading
          }
        >
          Submit Request
        </Button>
      )}{" "}
    </Box>
  );
};
const Step2 = ({ 
  form, 
  loading, 
  user, 
  bankAccountTypeRef,
  routingNumberRef,
  accountNumberRef,
}: FormStepProps) => {
  const { isDirty, errors, isSubmitting, isValid } = form.formState;
  const [nameOnAccount, setNameOnAccount] = useState(
    form.getValues("nameOnAccount") || ""
  );
  const [accountNumber, setAccountNumber] = useState(
    form.getValues("accountNumber") || ""
  );
  const [routingNumber, setRoutingNumber] = useState(
    form.getValues("routingNumber") || ""
  );
  const [bankFile, setBankFile] = useState<File | null>(
    form.getValues("bankFile") || null
  );

  // Define hasBankFieldDue based on your business logic
  const hasBankFieldDue = true; // Replace with actual logic


  return (
    <Box className="ath-personal-details">
      <Typography fontSize="17px" fontWeight="600" color="red" marginBottom={4}>
        {" "}
        After you submit your banking information, you will need to complete
        verification by our bank processor
      </Typography>
      <Header text="Payout info" />
      <Box sx={{ mt: 4 }} textAlign={"left"} className="personal-details__form">
        <Stack sx={{ mt: 2 }} direction="column" alignItems="flex-start">
          {/* Form fields remain unchanged */}
          <TextField
            sx={{ mt: 3 }}
            select
            margin="dense"
            data-testid="NameOnAccount"
            label="Payout Type"
            placeholder="Payout Type"
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            autoComplete="none"
            defaultValue={form.getValues("payoutType") || ""}
            inputProps={{
              "data-testid": "payoutType",
            }}
            error={Boolean(isDirty && errors.payoutType)}
            disabled={isSubmitting}
            helperText={(isDirty && errors.payoutType?.message) || " "}
            {...form.register("payoutType", {
              required: "Payout Type is required",
            })}
          >
            <MenuItem value={"ACH"}>ACH</MenuItem>
          </TextField>
          <Controller
            name="bankAccountType"
            control={form.control}
            rules={{ required: "Bank Account Type is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                select
                margin="dense"
                data-testid="NameOnAccount"
                label="Bank Account Type"
                placeholder="Bank Account Type"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                autoComplete="none"
                inputRef={bankAccountTypeRef}
                inputProps={{
                  "data-testid": "bankAccountType",
                }}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
              >
                <MenuItem value={"CHECKING"}>CHECKING</MenuItem>
                <MenuItem value={"SAVINGS"}>SAVINGS</MenuItem>
              </TextField>
            )}
          />
          <Controller
            name="routingNumber"
            control={form.control}
            rules={{ 
              required: "Routing Number is required",
              minLength: { value: 9, message: "Please Enter 9 minimum number" },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                margin="dense"
                data-testid="routingNumber"
                label="Routing Number"
                placeholder="Routing Number"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                autoComplete="none"
                inputRef={routingNumberRef}
                inputProps={{
                  "data-testid": "routingNumber",
                }}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
                onChange={(e) => {
                  const regex = /^[0-9\b]+$/;
                  if (e.target.value === "" || regex.test(e.target.value)) {
                    if (e.target.value.length < 10) {
                      field.onChange(e);
                    }
                  }
                }}
              />
            )}
          />
          <Controller
            name="accountNumber"
            control={form.control}
            rules={{ 
              required: "Bank Account Number is required",
              minLength: {
                value: 6,
                message: "Please enter 6 minimum digits number",
              },
              maxLength: {
                value: 34,
                message: "Please enter 34 max digits number",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                sx={{ mt: 3 }}
                margin="dense"
                data-testid="Bank Account Id"
                label="Bank Account Number"
                placeholder="Bank Account Number"
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                autoComplete="none"
                inputRef={accountNumberRef}
                inputProps={{
                  "data-testid": "accountNumber",
                }}
                error={Boolean(isDirty && error)}
                disabled={isSubmitting}
                helperText={(isDirty && error?.message) || " "}
                onChange={(e) => {
                  const regex = /^[0-9\b]+$/;
                  if (e.target.value === "" || regex.test(e.target.value)) {
                    field.onChange(e);
                  }
                }}
              />
            )}
          />
          <Box component={"span"} sx={{ mt: 3, width: "100%" }}>
            <Typography
              style={{ color: "#592EE5", width: "100%", display: "flex" }}
              component={"label"}
            >
              Upload bank statement
            </Typography>

            <FileUpload
              acceptFileType=".jpg,.jpeg,.png,.pdf"
              setFile={(f) => {
                setBankFile(f);
                form.setValue("bankFile", f);
              }}
              file={bankFile}
              maxFileSize={4000}
              formatErrorMessage="JPEG, JPG, PNG, or PDF"
            />
          </Box>
        </Stack>
      </Box>
      <Button
        startIcon={loading ? <CircularProgress size={20} /> : null}
        sx={{ mt: 8 }}
        type="submit"
        fullWidth
        color={"primary"}
        variant="contained"
        disabled={!isValid || !bankFile || loading}
      >
        Submit Request
      </Button>
    </Box>
  );
};

export default PersonalDetails;